/**
 * Generates example JSON from Smithy shape definitions for documentation purposes.
 *
 * This utility creates representative JSON examples that help developers understand
 * the structure of entity types without needing to find real data samples. The generated
 * examples use realistic placeholder values based on field names, types, and Smithy traits.
 *
 * ## Features
 * - Generates realistic placeholder values based on field names and types
 * - Handles recursive and self-referential types safely via depth limiting and cycle detection
 * - Respects Smithy traits like `@range`, `@pattern`, and `@timestampFormat`
 * - Produces valid JSON that conforms to the shape's structure
 *
 * ## Usage
 * ```kotlin
 * val shape: SmithyShape = model.getShape("com.example#MyEntity")
 * val exampleJson: JsonElement = shape.exampleJson()
 * println(exampleJson) // {"id": "string", "count": 123, ...}
 * ```
 *
 * @see SmithyShape.exampleJson
 */
package com.siriusxm.cmc.smithy

import com.siriusxm.smithy4kt.SmithyBoolean
import com.siriusxm.smithy4kt.SmithyEnum
import com.siriusxm.smithy4kt.SmithyList
import com.siriusxm.smithy4kt.SmithyMap
import com.siriusxm.smithy4kt.SmithyNumber
import com.siriusxm.smithy4kt.SmithyShape
import com.siriusxm.smithy4kt.SmithyString
import com.siriusxm.smithy4kt.SmithyStructure
import com.siriusxm.smithy4kt.SmithyTimestamp
import com.siriusxm.smithy4kt.SmithyUnion
import kotlinx.serialization.json.*
import kotlin.math.abs

/** Default maximum nesting depth for generated examples. Prevents overly deep JSON for complex models. */
private const val DEFAULT_MAX_EXAMPLE_DEPTH = 3
private const val SAMPLE_UUID = "827f657d-b290-449c-a18a-5f59ce83c5cf"
private const val SAMPLE_S3_URI = "s3://example-bucket/some/key"

/**
 * Generates an example JSON representation of this Smithy shape.
 *
 * The generated JSON contains representative placeholder values that illustrate
 * the structure of the shape. This is useful for documentation and understanding
 * what data conforming to this shape would look like.
 *
 * @param maxDepth Maximum nesting depth for recursive structures. Deeper nested objects
 *                 will show only required fields to prevent infinite expansion. Defaults to 3.
 * @return A [JsonElement] containing representative example values for this shape
 */
fun SmithyShape.exampleJson(maxDepth: Int = DEFAULT_MAX_EXAMPLE_DEPTH): JsonElement {
    return ExampleJsonGenerator(maxDepth).generate(this, owner = this)
}

/**
 * Internal generator that recursively builds example JSON from Smithy shapes.
 *
 * Uses a visitor-like pattern to handle each shape type differently. Tracks visited
 * shapes to prevent infinite recursion on circular references (e.g., a Person with
 * a "parent" field that references Person).
 *
 * @property maxDepth Maximum depth before switching to simplified placeholder generation
 */
private class ExampleJsonGenerator(private val maxDepth: Int) {

    /**
     * Recursively generates a JSON example for the given shape.
     *
     * @param shape The Smithy shape to generate an example for
     * @param depth Current recursion depth (0 = root level)
     * @param visited Set of shape IDs already processed in this branch, used for cycle detection
     * @param memberNameHint The field name from the parent structure, used to infer better
     *                       example values (e.g., "createdAt" suggests a timestamp format)
     * @return JSON representation with example values
     */
    fun generate(
        shape: SmithyShape,
        depth: Int = 0,
        visited: Set<String> = emptySet(),
        memberNameHint: String? = null,
        owner: SmithyShape? = null
    ): JsonElement {
        val shapeId = shape.id

        // Stop recursing if we've gone too deep or hit a cycle
        if (depth >= maxDepth || shapeId in visited) {
            return placeholderFor(shape, memberNameHint, visited, owner)
        }
        val nextVisited = visited + shapeId

        return when (shape) {
            is SmithyStructure -> structureExample(shape, depth, nextVisited, owner)
            is SmithyList -> listExample(shape, depth, nextVisited, owner)
            is SmithyMap -> mapExample(shape, depth, nextVisited, owner)
            is SmithyEnum -> enumExample(shape)
            is SmithyUnion -> unionExample(shape, depth, nextVisited, owner)
            is SmithyString -> JsonPrimitive(shape.stringExample(memberNameHint, owner))
            is SmithyBoolean -> JsonPrimitive(true)
            is SmithyTimestamp -> JsonPrimitive(timestampExample(shape))
            is SmithyNumber<*> -> JsonPrimitive(shape.numberExample(memberNameHint))
            else -> JsonNull
        }
    }

    /**
     * Generates an example for a structure shape by including all required members
     * and optional members (if they don't resolve to null).
     */
    private fun structureExample(
        structure: SmithyStructure,
        depth: Int,
        visited: Set<String>,
        owner: SmithyShape?
    ): JsonElement {
        val (requiredMembers, optionalMembers) = structure.allMembers.values.partition { it.required }
        return buildJsonObject {
            (requiredMembers + optionalMembers).forEach { member ->
                val rendered = generate(
                    member.shape,
                    depth + 1,
                    visited,
                    member.name,
                    owner = owner ?: structure
                )
                if (!member.required && rendered == JsonNull) return@forEach
                put(member.name, rendered)
            }
        }
    }

    /** Generates a list example with a single element. */
    private fun listExample(
        list: SmithyList,
        depth: Int,
        visited: Set<String>,
        owner: SmithyShape?
    ): JsonElement {
        return buildJsonArray {
            add(generate(list.member, depth + 1, visited, owner = owner))
        }
    }

    /** Generates a map example with a single key-value pair. */
    private fun mapExample(
        map: SmithyMap,
        depth: Int,
        visited: Set<String>,
        owner: SmithyShape?
    ): JsonElement {
        val key = mapKeyExample(map.key)
        val value = generate(map.value, depth + 1, visited, owner = owner)
        return buildJsonObject {
            put(key, value)
        }
    }

    /** Returns the first enum value, or "enumValue" if none defined. */
    private fun enumExample(enum: SmithyEnum): JsonElement {
        val value = enum.allEntries.firstOrNull()?.value ?: "enumValue"
        return JsonPrimitive(value)
    }

    /**
     * Generates a union example using the first member variant.
     * Note: Unions are represented as an object with a single key indicating the variant.
     */
    private fun unionExample(
        union: SmithyUnion,
        depth: Int,
        visited: Set<String>,
        owner: SmithyShape?
    ): JsonElement {
        val memberEntry = union.members.entries.firstOrNull() ?: return JsonObject(emptyMap())
        val memberValue = generate(
            memberEntry.value.shape,
            depth + 1,
            visited,
            memberEntry.key,
            owner = owner
        )
        return buildJsonObject {
            put(memberEntry.key, memberValue)
        }
    }

    /**
     * Creates a simplified placeholder when max depth is reached or a cycle is detected.
     * For complex types (structures, maps, lists), only required fields are included.
     * For primitive types, a simple example value is returned.
     */
    private fun placeholderFor(
        shape: SmithyShape,
        memberNameHint: String?,
        visited: Set<String>,
        owner: SmithyShape? = null
    ): JsonElement {
        return when (shape) {
            is SmithyStructure -> placeholderForStructure(shape, visited, owner)
            is SmithyMap -> placeholderForMap(shape, visited, owner)
            is SmithyList -> placeholderForList(shape, visited, owner)
            is SmithyUnion -> placeholderForUnion(shape, visited, owner)
            is SmithyEnum -> enumExample(shape)
            is SmithyBoolean -> JsonPrimitive(true)
            is SmithyNumber<*> -> JsonPrimitive(shape.numberExample(memberNameHint))
            is SmithyTimestamp -> JsonPrimitive(timestampExample(shape))
            is SmithyString -> JsonPrimitive(shape.stringExample(memberNameHint, owner))
            else -> JsonNull
        }
    }

    private fun placeholderForUnion(union: SmithyUnion, visited: Set<String> = emptySet(), owner: SmithyShape? = null): JsonElement {
        val memberEntry = union.members.entries.firstOrNull() ?: return JsonObject(emptyMap())
        val memberShape = memberEntry.value.shape
        val memberValue = when (memberShape) {
            is SmithyStructure -> placeholderForStructure(memberShape, visited, owner)
            else -> generate(memberShape, maxDepth, visited, memberEntry.key, owner = owner)
        }
        return buildJsonObject {
            put(memberEntry.key, memberValue)
        }
    }

    /** Creates a minimal structure with only required fields when at max depth. */
    private fun placeholderForStructure(
        structure: SmithyStructure,
        visited: Set<String>,
        owner: SmithyShape?
    ): JsonElement {
        val requiredMembers = structure.allMembers.values.filter { it.required }
        return buildJsonObject {
            requiredMembers.forEach { member ->
                put(member.name, generate(member.shape, maxDepth, visited, member.name, owner = owner ?: structure))
            }
        }
    }

    private fun placeholderForList(
        list: SmithyList,
        visited: Set<String>,
        owner: SmithyShape?
    ): JsonElement {
        return JsonArray(listOf(generate(list.member, maxDepth, visited, owner = owner)))
    }

    private fun placeholderForMap(
        map: SmithyMap,
        visited: Set<String>,
        owner: SmithyShape?
    ): JsonElement {
        val key = mapKeyExample(map.key)
        val value = generate(map.value, maxDepth, visited, owner = owner)
        return buildJsonObject { put(key, value) }
    }

    /** Generates an appropriate example string for use as a map key. */
    private fun mapKeyExample(keyShape: SmithyShape?): String {
        return when (keyShape) {
            is SmithyEnum -> keyShape.allEntries.firstOrNull()?.value ?: "key"
            is SmithyString -> if (keyShape.isTimestampLike(null)) timestampExample(keyShape) else "key"
            is SmithyNumber<*> -> "123"
            is SmithyBoolean -> "true"
            is SmithyTimestamp -> timestampExample(keyShape)
            else -> "key"
        }
    }

    /**
     * Returns a representative timestamp string based on the shape's format trait.
     * Uses April 10, 2025 at 16:35:00 UTC as a recognizable example date.
     */
    private fun timestampExample(shape: SmithyShape): String {
        return when (shape.timestampFormatTrait()) {
            "epoch-seconds" -> "1712764910.123"
            "http-date" -> "Tue, 10 Apr 2025 16:35:00 GMT"
            else -> "2025-04-10T16:35:00Z"  // ISO 8601 format (default)
        }
    }

    /**
     * Generates an example string value, respecting @pattern traits and detecting
     * timestamp-like fields by name.
     */
    private fun SmithyString.stringExample(
        memberNameHint: String?,
        owner: SmithyShape?
    ): String {
        val patternSample = pattern?.pattern?.let { exampleForPattern(it) }
        if (patternSample != null) {
            return patternSample
        }
        if (memberNameHint != null && memberNameHint.equals("type", ignoreCase = true)) {
            return owner?.id?.substringAfterLast("#") ?: "string"
        }
        return if (isTimestampLike(memberNameHint)) {
            timestampExample(this)
        } else {
            "string"
        }
    }

    /**
     * Detects if a shape represents a timestamp based on:
     * - Being a SmithyTimestamp type
     * - Having a @timestampFormat trait
     * - Having "timestamp", "time", or "date" in the shape ID or member name
     */
    private fun SmithyShape.isTimestampLike(memberNameHint: String?): Boolean {
        if (this is SmithyTimestamp) return true
        if (timestampFormatTrait() != null) return true
        val idHint = this.id.substringAfterLast("#").lowercase()
        if ("timestamp" in idHint || idHint.endsWith("time") || idHint.endsWith("date")) {
            return true
        }
        val nameHint = memberNameHint?.lowercase()
        if (nameHint != null && ("timestamp" in nameHint || nameHint.endsWith("time") || nameHint.endsWith("date"))) {
            return true
        }
        return false
    }

    /** Extracts the @timestampFormat trait value if present. */
    private fun SmithyShape.timestampFormatTrait(): String? {
        return this.traits?.get("smithy.api#timestampFormat")?.jsonPrimitive?.contentOrNull
    }

    /**
     * Generates an example number, respecting @range traits and common field name patterns.
     *
     * Priority order:
     * 1. If @range trait exists, use midpoint of min/max (or boundary if only one specified)
     * 2. If field name contains "opacity", "percent", or "ratio", use semantically appropriate value
     * 3. Otherwise, default to 123
     */
    private fun SmithyNumber<*>.numberExample(memberNameHint: String?): Number {
        val name = memberNameHint?.lowercase()
        val bounded = rangeTraitBounds()?.let { (min, max) ->
            when {
                min != null && max != null -> midpointOrInt(min, max)
                min != null -> min
                max != null -> minOf(max, 1.0)
                else -> null
            }
        }
        if (bounded != null) {
            return bounded
        }
        return when {
            name?.contains("opacity") == true -> 1    // Opacity typically 0-1
            name?.contains("percent") == true -> 100  // Percentage typically 0-100
            name?.contains("ratio") == true -> 1      // Ratios often normalized to 1
            else -> 123
        }
    }

    /** Extracts min/max bounds from @range trait if present. */
    private fun SmithyNumber<*>.rangeTraitBounds(): Pair<Double?, Double?>? {
        val range = this.traits?.get("smithy.api#range")?.jsonObject ?: return null
        val min = range["min"]?.jsonPrimitive?.doubleOrNull
        val max = range["max"]?.jsonPrimitive?.doubleOrNull
        return min to max
    }

    /** Returns the midpoint of two numbers, preserving integer type if both bounds are whole numbers. */
    private fun midpointOrInt(min: Double, max: Double): Number {
        val midpoint = (min + max) / 2
        return if (min.isWhole() && max.isWhole()) midpoint.toLong() else midpoint
    }

    /** Checks if a double is effectively a whole number (within floating point tolerance). */
    private fun Double.isWhole(): Boolean {
        return abs(this % 1.0) < 1e-9
    }
}

/**
 * Attempts to generate an example string that matches a regex [pattern].
 *
 * First tries a handful of hard-coded shapes for common patterns (UUID, S3 URI, semantic
 * version, duration, hex colour) that are cheaper and friendlier than a synthesised value,
 * then falls back to [exampleFromPattern], which walks the pattern and emits a matching
 * sample. Exposed at file scope (rather than buried in the generator) so callers that only
 * have a raw pattern string — e.g. the entity-type example id field — can reuse it, and so
 * it is unit-testable without a Compose or Smithy runtime.
 *
 * @return A string matching the pattern, or null if nothing could be synthesised.
 */
fun exampleForPattern(pattern: String): String? {
    val normalized = pattern.lowercase()
    // Priority order matters: more specific patterns must come before general ones
    // to avoid false positives from the contains() checks.
    return when {
        isUuidPattern(normalized) -> SAMPLE_UUID
        isS3Pattern(normalized) -> SAMPLE_S3_URI
        normalized.contains("([0-9]+)\\.([0-9]+)\\.([0-9]+)") ||
            normalized.contains("\\d+\\.\\d+\\.\\d+") -> "1.0.0"
        normalized.contains("([0-9]+)\\.([0-9]+)") ||
            normalized.contains("\\d+\\.\\d+") -> "1.0"
        normalized.contains("[0-9]+d") &&
            normalized.contains("[0-9]+h") &&
            normalized.contains("[0-9]+m") -> "1d2h30m"
        normalized.contains("#[0-9a-f]{6}") -> "#3366FF"
        else -> exampleFromPattern(pattern)
    }
}

/**
 * Synthesises a string that satisfies the regex [pattern] by walking it left to right.
 *
 * This is a deliberately small, best-effort subset of regex — enough to satisfy the
 * `@pattern` traits that appear on Smithy id fields (literal prefixes, character classes,
 * shorthand classes, `{n}` / `+` / `*` / `?` quantifiers, and the first branch of an
 * alternation or group). It is not a general regex engine; unrecognised constructs cause it
 * to return null rather than emit something that would not match.
 *
 * @return A string matching the pattern, or null if it could not be synthesised.
 */
fun exampleFromPattern(pattern: String): String? {
    val result = StringBuilder()
    var i = 0
    val chars = pattern.toCharArray()

    while (i < chars.size) {
        when {
            chars[i] == '^' || chars[i] == '$' -> i++

            chars[i] == '(' -> {
                i++
                // Skip non-capturing group prefix (?:, (?=, (?!, etc.)
                if (i < chars.size && chars[i] == '?') {
                    i++
                    if (i < chars.size && (chars[i] == ':' || chars[i] == '=' || chars[i] == '!')) i++
                }
                // Process first alternative only, skip the rest
                var depth = 1
                while (i < chars.size && depth > 0) {
                    when {
                        chars[i] == '(' -> { depth++; i++ }
                        chars[i] == ')' -> { depth--; if (depth > 0) i++ }
                        chars[i] == '|' -> {
                            // Skip all remaining alternatives until group closes
                            i++
                            while (i < chars.size && depth > 0) {
                                when (chars[i]) {
                                    '(' -> { depth++; i++ }
                                    ')' -> { depth--; if (depth > 0) i++ }
                                    else -> i++
                                }
                            }
                        }
                        else -> { i = processPatternToken(chars, i, result) ?: return null }
                    }
                }
                if (i < chars.size) i++ // skip ')'
                // Check for quantifier after group
                if (i < chars.size && chars[i] == '?') i++
                if (i < chars.size && chars[i] == '+') i++
                if (i < chars.size && chars[i] == '*') i++
            }

            chars[i] == '|' -> {
                // Alternation - just take what we have so far (first alternative)
                break
            }

            else -> {
                i = processPatternToken(chars, i, result) ?: return null
            }
        }
    }

    return result.toString().ifEmpty { null }
}

private fun processPatternToken(chars: CharArray, startIndex: Int, result: StringBuilder): Int? {
    var i = startIndex
    val sampleChar: Char?

    when {
        chars[i] == '\\' && i + 1 < chars.size -> {
            i++
            sampleChar = when (chars[i]) {
                'd' -> '1'
                'w' -> 'a'
                's' -> ' '
                '.' -> '.'
                '-' -> '-'
                '(' -> '('
                ')' -> ')'
                '[' -> '['
                ']' -> ']'
                '\\' -> '\\'
                else -> chars[i]
            }
            i++
        }

        chars[i] == '[' -> {
            i++
            sampleChar = parseCharacterClass(chars, i)
            // Skip to closing ]
            while (i < chars.size && chars[i] != ']') i++
            if (i < chars.size) i++ // skip ']'
        }

        chars[i] == '.' -> {
            sampleChar = 'x'
            i++
        }

        else -> {
            sampleChar = chars[i]
            i++
        }
    }

    if (sampleChar == null) return null

    // Check for quantifier
    val count = when {
        i < chars.size && chars[i] == '{' -> {
            var braceEnd = i + 1
            while (braceEnd < chars.size && chars[braceEnd] != '}') braceEnd++
            if (braceEnd >= chars.size) return null
            val quantifier = chars.concatToString(i + 1, braceEnd)
            i = braceEnd + 1
            // Handle {n}, {n,m} - use the minimum (or exact)
            quantifier.split(",")[0].trim().toIntOrNull() ?: return null
        }
        i < chars.size && chars[i] == '+' -> {
            i++
            3
        }
        i < chars.size && chars[i] == '*' -> {
            i++
            0
        }
        i < chars.size && chars[i] == '?' -> {
            i++
            1
        }
        else -> 1
    }

    repeat(count) { result.append(sampleChar) }
    return i
}

/**
 * Returns a single character that is a member of the character class beginning at [startIndex]
 * (just past the opening `[`). Handles ranges (A-Z, a-z, 0-9, hex), escaped shorthand classes
 * (`\d`, `\w`, `\s`), and literal members (the "T" in `[TRE]`). Negated classes fall back to
 * 'x' as a best-effort character outside the common ranges.
 */
private fun parseCharacterClass(chars: CharArray, startIndex: Int): Char? {
    var i = startIndex
    val negated = i < chars.size && chars[i] == '^'
    if (negated) i++
    // For a negated class we can't reliably pick a member; 'x' is a best-effort
    // character that is outside the common ranges we generate for.
    if (negated) return 'x'

    while (i < chars.size && chars[i] != ']') {
        val c = chars[i]
        when {
            // Range like A-Z, a-z, 0-9 — return the low end of the range.
            i + 2 < chars.size && chars[i + 1] == '-' && chars[i + 2] != ']' -> {
                val rangeStart = c
                val rangeEnd = chars[i + 2]
                return when {
                    rangeStart == 'A' && rangeEnd == 'Z' -> 'A'
                    rangeStart == 'a' && rangeEnd == 'z' -> 'a'
                    rangeStart == '0' && rangeEnd == '9' -> '1'
                    rangeStart == 'a' && rangeEnd == 'f' -> 'a'
                    rangeStart == 'A' && rangeEnd == 'F' -> 'A'
                    else -> rangeStart
                }
            }

            // Escaped shorthand inside the class, e.g. [\d], [\w].
            c == '\\' && i + 1 < chars.size -> return when (chars[i + 1]) {
                'd' -> '1'
                'w' -> 'a'
                's' -> ' '
                else -> chars[i + 1]
            }

            // A literal member of the class (e.g. the "T", "R", "E" in [TRE]).
            else -> return c
        }
    }
    return 'x'
}

private fun isUuidPattern(pattern: String): Boolean {
    if (!pattern.contains("-")) {
        return false
    }
    if (!(pattern.contains("[0-9a-f]{8}") &&
        pattern.contains("[0-9a-f]{4}") &&
        pattern.contains("[0-9a-f]{12}"))) {
        return false
    }
    // Reject if there's a literal prefix before the UUID (e.g., "^RE:[0-9a-f]{8}-...")
    val beforeUuid = pattern.substringBefore("[0-9a-f]{8}")
        .removePrefix("^")
        .replace("(", "")
    return beforeUuid.isEmpty()
}

private fun isS3Pattern(pattern: String): Boolean {
    return pattern.contains("s3://")
}
